import { MoreHorizontalIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/lib/auth/session';
import { cn } from '@/lib/utils';
import { AccountMenuItems } from '@/shared/components/AccountMenuItems';
import { UserAvatar } from '@/shared/components/UserAvatar';

/**
 * The signed-in person, at the foot of the rail (spec 067 FR-009).
 *
 * Was `WorkspaceOwner`, which named the wrong thing even before this: it reads the
 * session and nothing else, and it now renders in the organization console too, where
 * there is no workspace and no owner. One domain language means a component says what it
 * is (spec 074 FR-017).
 *
 * No longer the *only* place to sign out: the workspace list has its own
 * `AccountMenu`, and both reach `signOut` through the shared `AccountMenuItems`.
 * Anything that gates or changes signing out has to account for both surfaces.
 *
 * Identity is a `UserAvatar` — the same disc, tinted from the same id, that names
 * this person on a task card. It used to be a `bg-sidebar-accent` circle written
 * out here, while `AccountMenu` drew a `bg-muted` one and the card drew a third:
 * one person, three marks, which only became visible once any of them had colour.
 * The email is still the second line, because two colleagues can share a display
 * name and cannot share an address.
 *
 * `null` is a real state, not an error — it is what the moment before the session
 * resolves looks like, and what every component test that does not mount a session
 * provider sees.
 */
export function SignedInPerson({
  collapsed = false,
  className,
}: {
  collapsed?: boolean;
  className?: string;
}) {
  const { user } = useSession();

  const name = user ? user.displayName : 'Not signed in';
  const secondary = user ? user.email : '';

  /* `null` is a real state — see the note above — so the disc has to survive not
     having a person to draw, which is why this is not simply inlined below. */
  const avatar = user ? (
    <UserAvatar
      id={user.id}
      displayName={user.displayName}
      email={user.email}
      className="size-7 text-xs"
    />
  ) : (
    <span
      aria-hidden="true"
      className="bg-sidebar-accent text-sidebar-accent-foreground flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium"
    >
      –
    </span>
  );

  return (
    <div
      className={cn(
        'border-sidebar-border flex items-center gap-2 border-t pt-3',
        collapsed && 'justify-center',
        className,
      )}
    >
      {collapsed ? (
        /*
          Collapsed, the disc *is* the trigger. The menu used to live only in the
          branch below, so a collapsed rail rendered an `aria-hidden` circle and
          an `sr-only` name and nothing else — no way to reach Sign out by mouse
          or by keyboard, from any page whose rail was collapsed. The disc was
          the obvious thing to click and the one thing that did nothing.
        */
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={!user}
            aria-label={user ? `Account menu — ${name}` : 'Account menu'}
            className="focus-visible:ring-sidebar-ring rounded-full focus-visible:ring-3 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
          >
            {avatar}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            {/* `showIdentity` here, unlike the expanded branch: the name and
                address are not on screen to repeat. */}
            <AccountMenuItems showIdentity />
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <>
          {avatar}
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-sidebar-foreground truncate text-sm font-medium">
              {name}
            </span>
            {secondary && (
              <span
                className="text-muted-foreground truncate text-xs"
                title={secondary}
              >
                {secondary}
              </span>
            )}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={!user}
              aria-label="Account menu"
              className="text-muted-foreground hover:text-sidebar-foreground rounded-md p-1 disabled:pointer-events-none disabled:opacity-50"
            >
              <MoreHorizontalIcon
                aria-hidden="true"
                className="size-4"
                strokeWidth={1.5}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              {/* No `showIdentity`: the name and address are already on the row
                  this menu hangs off, so repeating them would be a duplicate
                  rather than a heading. */}
              <AccountMenuItems />
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
}
